import { AlertTriangle, CheckCircle, Copy, XCircle } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { HStack, VStack } from '../../../components/primitives';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ImportResult } from '../types';

interface ImportSummaryCardProps {
  result: ImportResult;
}

function StatItem({
  icon,
  label,
  count,
  color,
}: Readonly<{ icon: ReactNode; label: string; count: number; color: string }>) {
  return (
    <HStack spacing="md" align="center" style={styles.statRow}>
      {icon}
      <Text variant="body" color={colors.text.primary} style={styles.statLabel}>
        {label}
      </Text>
      <Text variant="h5" style={{ color }}>
        {count}
      </Text>
    </HStack>
  );
}

export function ImportSummaryCard({ result }: Readonly<ImportSummaryCardProps>) {
  const { t } = useTranslation();

  return (
    <VStack spacing="lg">
      <VStack spacing="sm" align="center">
        <CheckCircle size={40} color={colors.status.success.main} />
        <Text variant="h5" color={colors.text.primary}>
          {t('leadImport.summary.title')}
        </Text>
      </VStack>

      <View style={styles.statsContainer} testID="import_summary_stats">
        <StatItem
          icon={<CheckCircle size={18} color={colors.status.success.main} />}
          label={t('leadImport.summary.imported')}
          count={result.importedCount}
          color={colors.status.success.main}
        />
        <StatItem
          icon={<Copy size={18} color={colors.status.warning.main} />}
          label={t('leadImport.summary.duplicates')}
          count={result.duplicateCount}
          color={colors.status.warning.main}
        />
        <StatItem
          icon={<XCircle size={18} color={colors.status.error.main} />}
          label={t('leadImport.summary.errors')}
          count={result.errorCount}
          color={colors.status.error.main}
        />
      </View>

      {result.duplicateEmails && result.duplicateEmails.length > 0 && (
        <VStack spacing="sm">
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leadImport.summary.duplicateDetails')}
          </Text>
          <ScrollView style={styles.errorList} showsVerticalScrollIndicator>
            {result.duplicateEmails.slice(0, 20).map((email) => (
              <HStack key={email} spacing="sm" style={styles.errorRow}>
                <Copy size={12} color={colors.status.warning.main} />
                <Text
                  variant="caption"
                  color={colors.status.warning.light}
                  style={styles.errorText}
                >
                  {email}
                </Text>
              </HStack>
            ))}
            {result.duplicateEmails.length > 20 && (
              <Text variant="caption" color={colors.text.tertiary} style={styles.moreText}>
                {t('leadImport.summary.andMore', { count: result.duplicateEmails.length - 20 })}
              </Text>
            )}
          </ScrollView>
        </VStack>
      )}

      {result.errors.length > 0 && (
        <VStack spacing="sm">
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leadImport.summary.errorDetails')}
          </Text>
          <ScrollView style={styles.errorList} showsVerticalScrollIndicator>
            {result.errors.slice(0, 20).map((error, index) => (
              <HStack key={`error-${index}`} spacing="sm" style={styles.errorRow}>
                <AlertTriangle size={12} color={colors.status.error.main} />
                <Text variant="caption" color={colors.status.error.light} style={styles.errorText}>
                  {t('leadImport.summary.rowError', {
                    row: error.rowIndex,
                    message: error.message,
                  })}
                </Text>
              </HStack>
            ))}
            {result.errors.length > 20 && (
              <Text variant="caption" color={colors.text.tertiary} style={styles.moreText}>
                {t('leadImport.summary.andMore', { count: result.errors.length - 20 })}
              </Text>
            )}
          </ScrollView>
        </VStack>
      )}
    </VStack>
  );
}

const styles = StyleSheet.create({
  statsContainer: {
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.lg,
    gap: tokens.spacing.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  statRow: {
    paddingVertical: tokens.spacing.xs,
  },
  statLabel: {
    flex: 1,
  },
  errorList: {
    maxHeight: 150,
  },
  errorRow: {
    paddingVertical: tokens.spacing.xs,
  },
  errorText: {
    flex: 1,
  },
  moreText: {
    textAlign: 'center',
    paddingVertical: tokens.spacing.sm,
  },
});
